// Strip colours section (sections/af-strip-colours.liquid). The strip colour changes on its own, one colour every
// 1.6 seconds (the section's Seconds per colour), while the section is on screen; tapping a swatch or a fur button
// stops the cycle for good (until the page is reloaded). With Reduce Motion the colour doesn't change on its own.
// "View" beside the fur buttons opens the Classic trim in the fur on show, at its plain product address; as it's
// clicked, the strip colour on show becomes the visitor's remembered colour (arcticfur:strip-colour:v1, the product
// page's own memory), so the product page opens with it chosen. On desktop its row starts at the model photo's left
// edge, measured here as the photo is drawn inside its frame. (Until 30 Oct 2026 the
// section held on screen while the scroll position picked the colour.) The trim and the model's parka change
// together. The fur buttons (a segmented control with a sliding glass pill) switch which fur the model wears; a fur's
// photos load the first time its button is tapped (until then they're hidden, so the browser leaves them), and the
// swap waits until the new photo is ready. The first colour's photos come with the page; the others get their
// addresses once the page has finished loading, so on a slow connection they never hold up the hero, and then load
// the browser's own lazy way. The cycle only moves on to a colour whose photos have arrived, so it never fades to an
// empty frame (31 Oct 2026; a script used to hand out the addresses one step ahead of the cycle).

class AfStripColours extends HTMLElement {
  connectedCallback() {
    this.trims = [...this.querySelectorAll('.af-strips__trim')];
    this.jackets = [...this.querySelectorAll('.af-strips__jacket')];
    this.swatches = [...this.querySelectorAll('.af-strips__swatch')];
    this.furButtons = [...this.querySelectorAll('.af-strips__fur')];
    this.colourName = this.querySelector('.af-strips__name');
    this.count = this.trims.length;
    this.fur = 0;
    this.active = -1;
    this.setActive(0);

    this.buy = this.querySelector('[data-af-strips-buy]');
    try {
      this.links = JSON.parse(this.querySelector('[data-af-strips-links]')?.textContent || '[]');
    } catch {
      this.links = [];
    }
    this.updateBuy();
    const remember = () => {
      const colour = this.swatches[this.active]?.getAttribute('aria-label');
      if (!colour) return;
      try {
        localStorage.setItem('arcticfur:strip-colour:v1', JSON.stringify({ colour, at: Date.now() }));
      } catch {
        // Private browsing: the product page opens without a colour chosen
      }
    };
    this.buy?.addEventListener('click', remember);
    this.buy?.addEventListener('auxclick', remember);

    this.swatches.forEach((swatch) => {
      swatch.addEventListener('click', () => this.choose(Number(swatch.dataset.index)));
    });
    this.furButtons.forEach((button) => {
      button.addEventListener('click', () => {
        this.stopCycle();
        this.setFur(Number(button.dataset.fur));
      });
    });
    this.setUpPill();
    this.setUpRow();
    this.setUpCycle();

    // The other colours' photos, once the page has finished loading (and the browser has a moment)
    const later = () => (window.requestIdleCallback ? requestIdleCallback(() => this.givePhotos(this.fur), { timeout: 2000 }) : setTimeout(() => this.givePhotos(this.fur), 300));
    if (document.readyState === 'complete') later();
    else addEventListener('load', later, { once: true });
  }

  // Photos waiting for their address (data-src) get it: every trim, and the model photos of one fur
  givePhotos(fur) {
    [...this.trims, ...this.jacketsFor(fur)].forEach((image) => {
      if (!image.dataset.src) return;
      image.srcset = image.dataset.srcset;
      image.src = image.dataset.src;
      delete image.dataset.src;
      delete image.dataset.srcset;
    });
  }

  disconnectedCallback() {
    this.pillObserver?.disconnect();
    this.cycleObserver?.disconnect();
    this.rowObserver?.disconnect();
    clearInterval(this.cycle);
  }

  // One colour after another while at least a quarter of the section is on screen, carrying on from where it was
  setUpCycle() {
    if (this.count < 2 || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const seconds = Number(this.dataset.seconds) || 1.6;
    this.cycleObserver = new IntersectionObserver(([entry]) => {
      clearInterval(this.cycle);
      this.cycle = 0;
      if (!entry.isIntersecting || this.chosen) return;
      this.cycle = setInterval(() => {
        const next = (this.active + 1) % this.count;
        if (this.ready(next)) this.setActive(next);
      }, seconds * 1000);
    }, { threshold: 0.25 });
    this.cycleObserver.observe(this);
  }

  // A tapped swatch or fur button stops the cycle for good
  stopCycle() {
    this.chosen = true;
    clearInterval(this.cycle);
    this.cycle = 0;
  }

  choose(index) {
    this.stopCycle();
    this.givePhotos(this.fur);
    this.setActive(index);
  }

  // View opens the Classic trim in the fur on show
  updateBuy() {
    const link = this.links?.[this.pendingFur ?? this.fur];
    if (this.buy && link) this.buy.href = link;
  }

  // The model photo is drawn at its own shape inside its frame (contain, centred); the row under it starts at the
  // photo's left edge (af-design.css uses this from 700px)
  setUpRow() {
    const frame = this.querySelector('.af-strips__frame--model');
    if (!frame || !this.buy) return;
    const place = () => {
      const photo = this.jackets.find((jacket) => jacket.classList.contains('is-active')) || this.jackets[0];
      const ratio = (photo?.naturalWidth && photo.naturalHeight) ? photo.naturalWidth / photo.naturalHeight : 0.8;
      const width = Math.min(frame.clientWidth, frame.clientHeight * ratio);
      this.style.setProperty('--af-model-inset', `${Math.max(0, Math.round((frame.clientWidth - width) / 2))}px`);
    };
    this.rowObserver = new ResizeObserver(place);
    this.rowObserver.observe(frame);
    this.jackets.forEach((jacket) => jacket.addEventListener('load', place, { once: true }));
    place();
  }

  jacketsFor(fur) {
    return this.jackets.filter((jacket) => Number(jacket.dataset.fur) === fur);
  }

  // Both photos of a colour (the trim and the model in the fur on show) have arrived
  ready(index) {
    return [this.trims[index], this.jacketsFor(this.fur)[index]].every((image) => !image || (image.complete && image.naturalWidth > 0));
  }

  async setFur(fur) {
    if (fur === (this.pendingFur ?? this.fur)) return;
    this.pendingFur = fur;
    this.updateBuy();
    this.furButtons.forEach((button, i) => button.setAttribute('aria-pressed', String(i === fur)));
    this.movePill(fur);
    if (fur !== this.fur) {
      // Its photos join the page and get their addresses (and so start loading); the one on show first
      const jackets = this.jacketsFor(fur);
      jackets.forEach((jacket) => jacket.classList.add('is-fur'));
      const shown = jackets[this.active];
      if (shown) shown.loading = 'eager';
      this.givePhotos(fur);
      try {
        await shown?.decode();
      } catch {
        // Show it anyway; the browser paints it as soon as it arrives
      }
    }
    if (this.pendingFur !== fur) return;
    this.pendingFur = undefined;
    this.fur = fur;
    this.showJacket();
  }

  // The pill sits behind the chosen fur button, placed by its left and right edges. When it moves, the edge heading
  // towards the new button arrives in 320ms and the trailing edge in 440ms on the same easing, so the pill
  // stretches about 15% on the way and settles back to size. Both edges are animated from one call, so they start
  // on the same frame in every browser (with CSS transitions Safari starts one a frame late, which flickers).
  setUpPill() {
    this.pill = this.querySelector('.af-strips__pill');
    if (!this.pill || !this.furButtons.length) return;
    this.pillIndex = 0;
    this.placePill(0);
    this.pillObserver = new ResizeObserver(() => this.placePill(this.pillIndex));
    this.pillObserver.observe(this.pill.parentElement);
    requestAnimationFrame(() => this.pill.parentElement.classList.add('is-ready'));
  }

  pillEdges(index) {
    const track = this.pill.parentElement;
    const button = this.furButtons[index];
    return {
      left: `${button.offsetLeft}px`,
      right: `${track.clientWidth - button.offsetLeft - button.offsetWidth}px`,
    };
  }

  placePill(index) {
    this.pillAnimations?.forEach((animation) => animation.cancel());
    Object.assign(this.pill.style, this.pillEdges(index));
  }

  movePill(index) {
    if (!this.pill || index === this.pillIndex) return;
    const lead = index > this.pillIndex ? 'right' : 'left';
    const trail = lead === 'right' ? 'left' : 'right';
    const current = getComputedStyle(this.pill);
    const from = { left: current.left, right: current.right };
    const to = this.pillEdges(index);
    this.pillIndex = index;
    this.placePill(index);
    if (matchMedia('(prefers-reduced-motion: reduce)').matches || !this.pill.animate) return;
    const easing = 'cubic-bezier(.25, .9, .3, 1)';
    this.pillAnimations = [
      this.pill.animate([{ [lead]: from[lead] }, { [lead]: to[lead] }], { duration: 320, easing }),
      this.pill.animate([{ [trail]: from[trail] }, { [trail]: to[trail] }], { duration: 440, easing }),
    ];
  }

  showJacket() {
    this.jackets.forEach((jacket) => {
      const on = Number(jacket.dataset.fur) === this.fur && Number(jacket.dataset.index) === this.active;
      jacket.classList.toggle('is-active', on);
      jacket.setAttribute('aria-hidden', String(!on));
    });
  }

  setActive(index) {
    if (index === this.active) return;
    this.active = index;
    this.trims.forEach((trim, i) => {
      trim.classList.toggle('is-active', i === index);
      trim.setAttribute('aria-hidden', String(i !== index));
    });
    this.showJacket();
    this.updateBuy();
    this.swatches.forEach((swatch, i) => swatch.setAttribute('aria-pressed', String(i === index)));
    if (this.colourName) this.colourName.textContent = this.swatches[index]?.getAttribute('aria-label') ?? '';
  }
}

if (!customElements.get('af-strip-colours')) {
  customElements.define('af-strip-colours', AfStripColours);
}
